import {
  CharacterData,
  ComputedStats,
  Modifier,
  ModifierFormulaObject,
  ModifierCondition
} from '../types/character';
import { resolveEquipmentReference, ResolvedEquipmentItem } from './equipmentResolver';
import { resolveDomainCardReference, ResolvedDomainCard } from './contentResolver';
import { StructuredFormulaEvaluator, isStructuredFormula } from './structuredFormulaEvaluator';

export interface ModifierBreakdown {
  target: string;
  baseValue: number;
  finalValue: number;
  modifiers: Array<{
    id: string;
    source: string;
    operation: string;
    value: number;
    resolvedValue: number;
    description?: string;
  }>;
}

export class CharacterStatComputer {
  private character: CharacterData;
  private computedStats: ComputedStats | null = null;
  private pureComputedStats: ComputedStats | null = null; // Stats before manual overrides
  private equippedItemModifiers: Modifier[] = [];
  private domainCardModifiers: Modifier[] = [];
  private resolvedEquippedItems: Map<string, ResolvedEquipmentItem> = new Map();
  private resolvedDomainCards: Map<string, ResolvedDomainCard> = new Map();
  private modifierBreakdowns: Map<string, ModifierBreakdown> = new Map();
  private formulaEvaluator: StructuredFormulaEvaluator;

  constructor(character: CharacterData) {
    this.character = character;
    this.formulaEvaluator = new StructuredFormulaEvaluator(character);
  }

  async computeStats(): Promise<ComputedStats> {
    const result: ComputedStats = JSON.parse(JSON.stringify(this.character.baseStats));

    // Clear previous breakdowns
    this.modifierBreakdowns.clear();

    // Initialize breakdowns with base values
    this.initializeBreakdowns(result);

    // Pre-load equipped item modifiers first to check for armor
    await this.loadEquippedItemModifiers();

    // Load domain card modifiers
    await this.loadDomainCardModifiers();

    // Calculate level-based default thresholds if no armor is equipped
    this.applyLevelBasedThresholds(result);

    const activeModifiers = this.getActiveModifiers();
    const sortedModifiers = this.sortModifiersByPriority(activeModifiers);

    for (const modifier of sortedModifiers) {
      this.applyModifier(result, modifier);
    }

    // Update final values in breakdowns
    this.updateFinalBreakdownValues(result);

    // Store pure computed stats BEFORE applying manual overrides
    // This allows UI to show what the computed value would be without overrides
    this.pureComputedStats = JSON.parse(JSON.stringify(result));

    // Apply manual overrides last (they take priority over computed values)
    this.applyManualOverrides(result);

    this.computedStats = result;
    return result;
  }

  /**
   * Apply manual overrides from character.manualOverrides to computed stats
   * Manual overrides take priority over all computed values
   */
  private applyManualOverrides(result: ComputedStats): void {
    if (!this.character.manualOverrides) {
      return;
    }

    const overrides = this.character.manualOverrides;

    // Apply each override if it's defined
    if (overrides.evasion !== undefined) {
      result.evasion = overrides.evasion;
    }
    if (overrides.armor !== undefined) {
      result.armor = overrides.armor;
    }
    if (overrides.hpSlots !== undefined) {
      result.hpSlots = overrides.hpSlots;
    }
    if (overrides.stressSlots !== undefined) {
      result.stressSlots = overrides.stressSlots;
    }
    if (overrides.hope !== undefined) {
      result.hope = overrides.hope;
    }
    if (overrides.thresholdMajor !== undefined) {
      result.thresholds.major = overrides.thresholdMajor;
    }
    if (overrides.thresholdSevere !== undefined) {
      result.thresholds.severe = overrides.thresholdSevere;
    }
  }

  private applyLevelBasedThresholds(result: ComputedStats): void {
    // Check if any armor is equipped using resolved items
    const hasArmorEquipped = Array.from(this.resolvedEquippedItems.values())
      .some(item => item.type === 'armor');

    // Only apply level-based thresholds if no armor is equipped
    if (!hasArmorEquipped) {
      const level = parseInt(this.character.level) || 1;
      result.thresholds.major = level;
      result.thresholds.severe = level * 2;
    }
  }

  private async loadEquippedItemModifiers(): Promise<void> {
    this.equippedItemModifiers = [];
    this.resolvedEquippedItems.clear();

    if (!this.character.inventory?.equipmentReferences) {
      return;
    }

    const equippedReferences = this.character.inventory.equipmentReferences
      .filter(ref => ref.equippedCount > 0);

    for (const ref of equippedReferences) {
      try {
        const resolvedItem = await resolveEquipmentReference(ref.itemId);
        if (resolvedItem) {
          // Store resolved item for condition checking
          this.resolvedEquippedItems.set(ref.itemId, resolvedItem);

          // Add modifiers for each equipped instance
          if (resolvedItem.modifiers) {
            for (let i = 0; i < ref.equippedCount; i++) {
              // Create unique modifier IDs to prevent conflicts
              const instanceModifiers = resolvedItem.modifiers.map((mod, index) => ({
                ...mod,
                id: `${ref.itemId}_${i}_${index}`,
                source: `equipment:${ref.itemId}:${i}`
              }));
              this.equippedItemModifiers.push(...instanceModifiers);
            }
          }
        }
      } catch (error) {
        console.warn(`Failed to resolve equipment modifiers for ${ref.itemId}:`, error);
      }
    }
  }

  private async loadDomainCardModifiers(): Promise<void> {
    this.domainCardModifiers = [];
    this.resolvedDomainCards.clear();
    
    // Only process loadout cards - vault cards are inactive and don't affect stats
    if (!this.character.inventory?.domainCardLoadout) {
      return;
    }
    
    for (const ref of this.character.inventory.domainCardLoadout) {
      try {
        const resolvedCard = await resolveDomainCardReference(ref.contentId);
        if (resolvedCard) {
          // Store resolved card for condition checking
          this.resolvedDomainCards.set(ref.contentId, resolvedCard);
          
          // Add modifiers from the domain card
          if (resolvedCard.modifiers) {
            const cardModifiers = resolvedCard.modifiers.map((mod, index) => ({
              ...mod,
              id: `${ref.contentId}_${index}`,
              source: `domain_card:${ref.contentId}`
            }));
            this.domainCardModifiers.push(...cardModifiers);
          }
        }
      } catch (error) {
        console.warn(`Failed to resolve domain card modifiers for ${ref.contentId}:`, error);
      }
    }
  }
  
  private getActiveModifiers(): Modifier[] {
    const modifiers: Modifier[] = [];

    // Add pre-loaded equipped item modifiers
    modifiers.push(...this.equippedItemModifiers);

    // Add pre-loaded domain card modifiers (replaces activeSpells)
    modifiers.push(...this.domainCardModifiers);

    this.character.classFeatures
      .filter(feature => feature.active)
      .forEach(feature => modifiers.push(...feature.modifiers));

    modifiers.push(...this.character.activeModifiers);

    return modifiers.filter(mod => this.meetsConditions(mod));
  }

  private sortModifiersByPriority(modifiers: Modifier[]): Modifier[] {
    return [...modifiers].sort((a, b) => a.priority - b.priority);
  }

  private applyModifier(stats: ComputedStats, modifier: Modifier): void {
    const targetPath = modifier.target.split('.');
    const current = this.getNestedValue(stats, targetPath);
    const resolvedValue = this.resolveValue(modifier.value, stats);
    let newValue: number;

    switch (modifier.operation) {
      case 'add':
        newValue = current + resolvedValue;
        break;
      case 'multiply':
        newValue = current * resolvedValue;
        break;
      case 'set':
        newValue = resolvedValue;
        break;
      case 'replace':
        newValue = resolvedValue;
        break;
      default:
        throw new Error(`Unknown modifier operation: ${modifier.operation}`);
    }

    this.setNestedValue(stats, targetPath, newValue);
    
    // Track this modifier in the breakdown
    this.addToBreakdown(modifier.target, modifier, resolvedValue, stats);
  }

  private resolveValue(value: number | ModifierFormulaObject, stats: ComputedStats): number {
    if (typeof value === 'number') {
      return value;
    }
    
    if (isStructuredFormula(value)) {
      return this.formulaEvaluator.evaluateFormulaObject(value, stats);
    }
    
    throw new Error(`Invalid modifier value type: ${JSON.stringify(value)}`);
  }


  private meetsConditions(modifier: Modifier): boolean {
    if (!modifier.conditions || modifier.conditions.length === 0) {
      return true;
    }

    return modifier.conditions.every(condition => this.evaluateCondition(condition));
  }

  private evaluateCondition(condition: ModifierCondition): boolean {
    switch (condition.type) {
      case 'equipment_not_equipped':
        // Check if any equipped item matches the specified category
        const hasEquippedItemOfType = Array.from(this.resolvedEquippedItems.values())
          .some(item => item.type === condition.category);
        return !hasEquippedItemOfType;
      
      case 'equipment':
        // Check if character has equipped item matching the condition
        if (!condition.category) return false;
        const hasEquippedItem = Array.from(this.resolvedEquippedItems.values())
          .some(item => item.type === condition.category);
        
        // If condition specifies equipped: true, return whether we have equipped item
        // If condition specifies equipped: false, return whether we DON'T have equipped item
        if (condition.equipped !== undefined) {
          return condition.equipped ? hasEquippedItem : !hasEquippedItem;
        }
        
        // Default behavior: return true if we have equipped item of this category
        return hasEquippedItem;
      
      case 'domain_card_count':
        // Count domain cards of the specified domain using resolved domain cards
        if (!condition.domain || condition.count === undefined) return false;
        const domainCardCount = Array.from(this.resolvedDomainCards.values())
          .filter(card => card.domain === condition.domain)
          .length;
        return domainCardCount >= condition.count;
      
      case 'stat_greater_than':
        if (!condition.stat || condition.value === undefined) return false;
        const statValue = this.getNestedValue(this.character.baseStats, condition.stat.split('.'));
        return statValue > condition.value;
      
      case 'stat_less_than':
        if (!condition.stat || condition.value === undefined) return false;
        const statValueLt = this.getNestedValue(this.character.baseStats, condition.stat.split('.'));
        return statValueLt < condition.value;
      
      case 'tier_equals':
        if (condition.value === undefined) return false;
        return this.character.tier === condition.value;
      
      default:
        console.warn(`Unknown condition type: ${condition.type}`);
        return false;
    }
  }

  private getNestedValue(obj: any, path: string[]): number {
    let current = obj;
    for (const key of path) {
      if (current[key] === undefined) {
        throw new Error(`Property ${path.join('.')} not found`);
      }
      current = current[key];
    }
    return Number(current);
  }

  private setNestedValue(obj: any, path: string[], value: number): void {
    let current = obj;
    for (let i = 0; i < path.length - 1; i++) {
      if (current[path[i]] === undefined) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }
    current[path[path.length - 1]] = value;
  }

  private initializeBreakdowns(baseStats: ComputedStats): void {
    // Initialize breakdowns for all trackable stats
    const trackableStats = [
      'traits.agility',
      'traits.strength', 
      'traits.finesse',
      'traits.instinct',
      'traits.presence',
      'traits.knowledge',
      'baseEvasion',
      'armor',
      'armorScore',
      'hpSlots',
      'stressSlots',
      'hope',
      'thresholds.major',
      'thresholds.severe'
    ];

    for (const stat of trackableStats) {
      try {
        const baseValue = this.getNestedValue(baseStats, stat.split('.'));
        this.modifierBreakdowns.set(stat, {
          target: stat,
          baseValue,
          finalValue: baseValue,
          modifiers: []
        });
      } catch (error) {
        // Skip stats that don't exist in base stats
        continue;
      }
    }
  }

  private addToBreakdown(target: string, modifier: Modifier, resolvedValue: number, stats: ComputedStats): void {
    const breakdown = this.modifierBreakdowns.get(target);
    if (breakdown) {
      const sourceString = typeof modifier.source === 'string' 
        ? modifier.source 
        : `${modifier.source?.type || 'unknown'}:${modifier.source?.name || 'unnamed'}`;
      
      // Create description for formula-based values
      let description: string | undefined;
      if (isStructuredFormula(modifier.value)) {
        description = this.formulaEvaluator.getFormulaDescription(modifier.value, stats);
      }
        
      breakdown.modifiers.push({
        id: modifier.id,
        source: sourceString,
        operation: modifier.operation,
        value: typeof modifier.value === 'number' ? modifier.value : 0,
        resolvedValue,
        description
      });
    }
  }

  private updateFinalBreakdownValues(computedStats: ComputedStats): void {
    this.modifierBreakdowns.forEach((breakdown, target) => {
      try {
        breakdown.finalValue = this.getNestedValue(computedStats, target.split('.'));
      } catch (error) {
        // Keep the base value if we can't get the computed value
      }
    });
  }

  getModifierBreakdown(target: string): ModifierBreakdown | undefined {
    return this.modifierBreakdowns.get(target);
  }

  getAllModifierBreakdowns(): Map<string, ModifierBreakdown> {
    return new Map(this.modifierBreakdowns);
  }

  /**
   * Get pure computed stats (before manual overrides are applied)
   * Used by ConfigurationPanel to show what the computed value would be in reset buttons
   */
  getPureComputedStats(): ComputedStats | null {
    return this.pureComputedStats;
  }
}